import { Box } from "@chakra-ui/react";
import React, { FC, useEffect, useMemo } from "react";
import { Editor } from "@tinymce/tinymce-react";
import { registerTinymceModalFocusFix } from "./tinymceChakraModalFix";
import { TINYMCE_I18N_PATCHES } from "./tinymceI18nPatches";
import { getBlockFormatsForLocale, resolveTinymceUiLocale } from "./tinymceLocale";
import "./textEditor.css";

interface TextEditorProps {
  onChange: (value: string) => void;
  value: string;
  id?: string;
  height?: string;
  error?: boolean;
  /** e.g. `i18n.language` — translates Tiny toolbars/dialogs when lang packs are available */
  contentLocale?: string;
}

/** Align stored empty state with prior Quill behaviour — strip meaningless empty blocks only. */
function normalizeEmptyHtml(html: string): string {
  const trimmed = html.trim();
  if (!trimmed) return "";
  if (/<img\b/i.test(trimmed)) return html;
  if (/<video\b/i.test(trimmed)) return html;
  if (/<iframe\b/i.test(trimmed)) return html;
  const text = trimmed
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/gi, " ")
    .replace(/\u00a0/g, " ")
    .trim();
  if (!text) return "";
  return html;
}

function parseHeightPx(height?: string): number | undefined {
  if (!height?.trim()) return undefined;
  const px = /^(\d+)px$/i.exec(height.trim());
  if (px) return parseInt(px[1], 10);
  const n = parseInt(height, 10);
  return Number.isFinite(n) ? n : undefined;
}

//TODO How to make a chakra component of this so it also get the focus border etc?

export const TextEditor: FC<TextEditorProps> = ({
  onChange,
  value,
  id = "0",
  height,
  error,
  contentLocale,
}) => {
  const safeValue = typeof value === "string" ? value : "";
  const parsedHeight = parseHeightPx(height);
  const tinymceUi = useMemo(
    () => resolveTinymceUiLocale(contentLocale),
    [contentLocale]
  );
  const blockFormats = useMemo(
    () => getBlockFormatsForLocale(contentLocale),
    [contentLocale]
  );

  useEffect(() => {
    const unregister = registerTinymceModalFocusFix();
    return unregister;
  }, []);

  const init = useMemo(() => {
    const cfg: Record<string, unknown> = {
      /** Self-hosted bundle copied to `/tinymce` by admin webpack; required for skins/plugins/lang paths */
      base_url: "/tinymce",
      suffix: ".min",
      height: parsedHeight ?? 256,
      min_height: 128,
      max_height: 512,
      menubar: false,
      branding: false,
      promotion: false,
      statusbar: false,
      plugins: "lists link",
      toolbar:
        "undo redo | blocks | bold italic underline | alignleft aligncenter alignright | bullist numlist | link",
      block_formats: blockFormats,
      content_style:
        "body { font-family: Helvetica, Arial, sans-serif; font-size: 14px; }",
      resize: true,
      browser_spellcheck: true,
      setup: (editor: {
        on: (ev: string, fn: () => void) => void;
      }) => {
        const applyPatches = () => {
          const lang = tinymceUi.language;
          const patch = lang ? TINYMCE_I18N_PATCHES[lang] : undefined;
          const api = (
            typeof window !== "undefined"
              ? (
                  window as Window & {
                    tinymce?: {
                      util: {
                        I18n: {
                          add: (
                            code: string,
                            data: Record<string, string>
                          ) => void;
                        };
                      };
                    };
                  }
                ).tinymce
              : undefined
          );
          if (lang && patch && api?.util?.I18n) {
            api.util.I18n.add(lang, patch);
          }
        };
        editor.on("Init", applyPatches);
      },
    };
    if (tinymceUi.language && tinymceUi.language_url) {
      cfg.language = tinymceUi.language;
      cfg.language_url = tinymceUi.language_url;
    }
    return cfg;
  }, [
    blockFormats,
    parsedHeight,
    tinymceUi.language,
    tinymceUi.language_url,
  ]);

  const handleEditorChange = (content: string) => {
    onChange(normalizeEmptyHtml(content));
  };

  return (
    <Box
      className="mtb-text-editor-root"
      maxW="100%"
      w="100%"
      borderWidth={error ? "1px" : undefined}
      borderColor={error ? "#E53E3E" : undefined}
      boxShadow={error ? "0 0 0 1px #e53e3e" : undefined}
      borderRadius={error ? "8px" : undefined}
    >
      <Editor
        key={`${id}-${tinymceUi.localeKey}`}
        id={id}
        licenseKey="gpl"
        tinymceScriptSrc="/tinymce/tinymce.min.js"
        value={safeValue}
        onEditorChange={handleEditorChange}
        init={init}
      />
    </Box>
  );
};
